'use client';

import type { UiState } from 'instantsearch.js';
import type QueryString from 'qs';
import type { UseNumericMenuProps, UseRangeProps, UseRefinementListProps } from 'react-instantsearch';
import {
    useNumericMenu,
    usePagination,
    useRange,
    useRefinementList,
    useSearchBox,
    useSortBy,
} from 'react-instantsearch';

import type {
    IAlgoliaRefinement,
    IAlgoliaRefinementList,
    IAlgoliaRouteState,
    IAlgoliaUIState,
    IVirtualSortProps,
} from '@libs/algolia';
import {
    AlgoliaIndice,
    FILTERS,
    getSortUrlValueFromIndex,
    isEligibleValue,
    ORDERED_FILTERS,
    SORT_INDEX_PLP,
    SORT_INDEX_SEARCH,
} from '@libs/algolia';
import { decodeString } from '@libs/common-next';

import { BROWSE_DEFAULT_SORT, SEARCH_DEFAULT_SORT } from '@/constants/algolia-sorts';
import type { Facets } from '@/types/facetedNavigation';

export const CATEGORIES_ATTRIBUTE = 'custom_category_names';

export const getQueryParamsFromState = (
    routeState: UiState,
    isSearchPage: boolean,
    isBrowseDrrEnabled = false
): IAlgoliaRouteState => {
    const queryParams: IAlgoliaRouteState = {};
    ORDERED_FILTERS.filter(
        e =>
            !e.isNumeric &&
            e.attribute !== FILTERS.BRAND.ATTRIBUTE &&
            e.attribute !== FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE &&
            e.attribute !== FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE
    ).forEach((element: IAlgoliaRefinement) => {
        if (routeState[element.attribute]) {
            const rawValues = routeState[element.attribute] as string[];
            const valuesForUrl =
                element.attribute === FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE
                    ? rawValues.filter(v => isEligibleValue(v))
                    : rawValues;
            if (valuesForUrl.length > 0) {
                queryParams[element.urlName] = valuesForUrl;
            }
        }
    });

    if (routeState.q) {
        queryParams.q = routeState.q as string;
    }

    if (routeState.page && Number(routeState.page) !== 1) {
        queryParams.page = routeState.page.toString();
    }

    if (routeState.customBvAverageRating) {
        queryParams.rating = routeState.customBvAverageRating.toString();
    }

    if (routeState[FILTERS.RANGE.ATTRIBUTE]) {
        queryParams.price = routeState[FILTERS.RANGE.ATTRIBUTE].toString();
    }

    if (isSearchPage && routeState.sort) {
        queryParams.sort = getSortUrlValueFromIndex(routeState.sort as string) ?? '';
    }

    if (!isSearchPage && routeState.sort) {
        const sortRule = getSortUrlValueFromIndex(routeState.sort as string) ?? '';
        const browseDefaultSort = isBrowseDrrEnabled ? SEARCH_DEFAULT_SORT : BROWSE_DEFAULT_SORT;
        if (sortRule !== browseDefaultSort) {
            queryParams.sort = sortRule;
        }
    }

    if (!isSearchPage && !routeState.sort && !isBrowseDrrEnabled) {
        queryParams.sort = SEARCH_DEFAULT_SORT;
    }

    return queryParams;
};

export const setGeneralQuery = (refinements: IAlgoliaRefinementList, q?: string) => {
    if (q) {
        refinements.query = decodeString(q as string);
    }

    return refinements;
};

export const getRefinementsFromQueryParams = (parsedqs: QueryString.ParsedQs, filterMap: Facets[]): UiState => {
    let refinements: IAlgoliaRefinementList = {};
    refinements = Object.assign({}, ...(filterMap as Facets[]));
    return setGeneralQuery(refinements, parsedqs.q as string) as UiState;
};

export const getRouteFromState = (indexUiState: IAlgoliaUIState) => {
    const refinements: IAlgoliaRefinementList = {};
    if (indexUiState.refinementList) {
        ORDERED_FILTERS.filter(e => !e.isNumeric).forEach((element: IAlgoliaRefinement) => {
            refinements[element.attribute] = indexUiState.refinementList[element.attribute];
        });
    }

    if (indexUiState.query) {
        refinements.q = indexUiState.query;
    }

    if (indexUiState.page) {
        refinements.page = indexUiState.page;
    }

    if (indexUiState.numericMenu?.customBvAverageRating) {
        refinements.customBvAverageRating = indexUiState.numericMenu.customBvAverageRating;
    }

    if (indexUiState.range?.[FILTERS.RANGE.ATTRIBUTE] && indexUiState.range[FILTERS.RANGE.ATTRIBUTE] !== ':0') {
        refinements[FILTERS.RANGE.ATTRIBUTE] = indexUiState.range[FILTERS.RANGE.ATTRIBUTE];
    }

    if (indexUiState.sortBy) {
        refinements.sort = indexUiState.sortBy;
    }

    if (indexUiState.refinementList?.[CATEGORIES_ATTRIBUTE]) {
        refinements[CATEGORIES_ATTRIBUTE] = indexUiState.refinementList[CATEGORIES_ATTRIBUTE];
    }

    return refinements as UiState;
};

export const getStateFromRoute = (
    routeState: IAlgoliaRouteState,
    isSearchPage: boolean,
    isBrowseDrrEnabled = false
) => {
    const refinementList: IAlgoliaRefinementList = {};
    ORDERED_FILTERS.filter(e => !e.isNumeric).forEach((element: IAlgoliaRefinement) => {
        const raw = routeState[element.urlName];
        let values: string[] | undefined;
        if (element.attribute === FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE) {
            if (raw === undefined || raw === '') {
                values = undefined;
            } else if (Array.isArray(raw)) {
                values = raw as string[];
            } else {
                values = [String(raw)];
            }
            if (values?.length) {
                const eligibleOnly = values.filter(v => isEligibleValue(v));
                values = eligibleOnly.length > 0 ? eligibleOnly : undefined;
            }
        } else {
            values = routeState[element.urlName] as string[] | undefined;
        }
        refinementList[element.attribute] = values;
        if (
            element.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE &&
            routeState[element.urlName] &&
            (routeState[element.urlName] as string[])?.length !== 0
        ) {
            refinementList[FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE] = [FILTERS.BOPIS_ELIGIBLE.DEFAULT_VALUE];
        }
    });

    const numericMenu = {
        customBvAverageRating:
            routeState.customBvAverageRating && !Array.isArray(routeState.customBvAverageRating)
                ? routeState.customBvAverageRating
                : null,
    };

    const priceRange = routeState[FILTERS.RANGE.ATTRIBUTE];

    const range = {
        [FILTERS.RANGE.ATTRIBUTE]: priceRange && !Array.isArray(priceRange) ? priceRange : null,
    };

    let { sort } = routeState;
    if (sort === '') {
        sort = isSearchPage || isBrowseDrrEnabled ? AlgoliaIndice.PRODUCTS : AlgoliaIndice.PRODUCTS_BEST_SELLERS;
    }

    return {
        refinementList,
        page: routeState.page,
        sortBy: sort,
        numericMenu,
        range,
        query: routeState.query,
    } as UiState;
};

// virtual refinements for proper InstantSearchNext functionality
export function VirtualRefinementList(props: UseRefinementListProps) {
    useRefinementList(props);
    return null;
}
export function VirtualPagination() {
    usePagination();
    return null;
}
export function VirtualSortBy({ isSearchPage }: IVirtualSortProps) {
    useSortBy(isSearchPage ? SORT_INDEX_SEARCH : SORT_INDEX_PLP);
    return null;
}
export function VirtualNumericMenu(props: UseNumericMenuProps) {
    useNumericMenu(props);
    return null;
}
export function VirtualRangeInput(props: UseRangeProps) {
    useRange(props);
    return null;
}
export function VirtualSearchBox() {
    useSearchBox();
    return null;
}
